<template>
  <div
    v-click-outside="hideMenu"
    @click="showMenu"
    ref="cardRef"
    :class="isActionable ? 'tw:hover:cursor-pointer' : 'tw:hover:cursor-default'"
    class="tw:relative tw:bg-white tw:rounded-xl tw:border tw:border-gray-300 tw:py-[11px] tw:px-3 tw:flex tw:gap-x-4 tw:items-stretch"
  >
    <div class="tw:flex tw:flex-row tw:items-start tw:flex-1" :class="{ 'tw:gap-x-3': icon }">
      <img :src="icon" alt="" role="presentation" class="tw:flex-shrink-0" />
      <div
        class="tw:flex tw:flex-col tw:flex-1 tw:min-h-full tw:md:justify-between"
        :class="{ 'tw:justify-between': !isCancelledSubscription }"
      >
        <div class="tw:flex tw:items-center tw:mb-2" v-if="title">
          <h6 class="tw:font-bold tw:text-sm">{{ title }}</h6>
          <UiTooltip v-if="withHint" theme="customdarkwithpadding" tooltip-position="top" :label="hint">
            <InformationOutline class="tw:fill-none tw:stroke-gray-500 tw:h-[18px] tw:w-[18px] tw:ml-1" />
          </UiTooltip>
        </div>
        <div class="tw:self-start">
          <slot name="cardData" />
        </div>
      </div>
    </div>

    <button
      v-if="isActionable"
      class="tw:hover:cursor-pointer tw:hover:bg-gray-200 tw:rounded-md tw:self-center tw:flex-shrink-0"
      @click.stop="showMenu"
      type="button"
      aria-label="Toggle menu"
    >
      <img :src="dots" alt="" role="presentation" />
    </button>

    <Teleport to="body" :disabled="!isMobile">
      <div
        v-show="isMenuOpen(type)"
        @click.stop
        :style="isMobile ? dropdownMobileStyle : {}"
        :class="[
          'tw:bg-white shadow tw:rounded-lg tw:z-50 tw:max-h-[300px] tw:overflow-auto',
          isMobile
            ? 'tw:fixed tw:left-4 tw:right-4 tw:mx-auto tw:max-w-[calc(100vw-2rem)]'
            : 'tw:absolute tw:top-full tw:left-0 tw:right-0 tw:w-full tw:mt-1',
        ]"
      >
        <slot name="dropdownMenu" :closeMenu="hideMenu" />
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'

import { useIsMobile } from '@/composables/useIsMobile'
import { useCardMenu } from '@/components/studioPlus/insights/useCardMenu'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { selectedAccommodationId } from '@/layouts/queries'

const cardRef = ref(null)
const props = defineProps({
  icon: URL,
  title: String,
  hint: String,
  type: String,
  withHint: Boolean,
  isActionable: Boolean,
})

const dots = new URL(`../../../assets/img/rate-connect/dropdown-dots.svg`, import.meta.url)

const { toggleMenu, isMenuOpen, closeMenu } = useCardMenu()
const { isMobile } = useIsMobile()

const { isCancelledSubscription } = useStudioPlusCampaign(selectedAccommodationId)
const dropdownMobileStyle = computed(() => {
  if (!cardRef.value || !isMobile.value) return {}

  const rect = cardRef.value.getBoundingClientRect()
  return {
    top: `${rect.bottom + 8}px`,
  }
})

const showMenu = () => {
  if (props.isActionable) {
    toggleMenu(props.type)
  }
}

const hideMenu = () => {
  if (isMenuOpen(props.type)) {
    closeMenu()
  }
}

const handleScroll = () => {
  if (isMenuOpen(props.type)) {
    closeMenu()
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })

  const scrollContainer = cardRef.value?.closest('.tw\\:overflow-x-auto')
  if (scrollContainer) {
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
  }
})

onBeforeUnmount(() => {
  closeMenu()
  window.removeEventListener('scroll', handleScroll)

  const scrollContainer = cardRef.value?.closest('.tw\\:overflow-x-auto')
  if (scrollContainer) {
    scrollContainer.removeEventListener('scroll', handleScroll)
  }
})
</script>
